import '@lmt/unicorn-shared/app/components/global-forms/GlobalFormContent.scss';
import { withStartErrorCheck } from '@lmt/unicorn-shared/app/components/screen-plugs/StartErrorInspector';
import WelcomePage from '@lmt/unicorn-shared/app/components/WelcomePage';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useIsAuthorized } from '@lmt/unicorn-shared/app/features/auth/operations';
import 'devextreme/dist/css/dx.light.css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, RouteObject } from 'react-router';
import { createBrowserRouter, createHashRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import appStyles from './App.module.scss';
import { Loading } from './components/common/Loading';
import { Launcher } from './components/service/startup/launcher/Launcher';
import './helpers/cached-images';
import { ResetPasswordPage } from '@lmt/unicorn-shared/app/components/reset-password-page/ResetPasswordPage';
import { AuthenticatedApp } from './AuthenticatedApp';
import { lazyRetry } from './helpers/retry-helper';
import { RouterProvider } from 'react-router-dom';
import { ReactRouterErrorBoundary } from './helpers/router/ReactRouterErrorBoundary';
import { PatientListPage } from './components/patients/list/PatientListPage';
import { CalendarPage } from './components/calendar/CalendarPage';
import Dashboard from './components/dashboard/Dashboard';
import PatientProfile from './components/patients/profile/PatientProfile';
import { AbsencesAndHolidaysPage } from './components/absences/AbsencesAndHolidaysPage';
import { AuditPage } from './components/audit/Audit';
import { ColorSettingsPage } from './components/calendar/color-settings/ColorSettingsPage';
import { VisitCardSettingsPage } from './components/calendar/visit-card-settings/VisitCardSettingsPage';
import { DoctorsList } from './components/doctors/list/DoctorsList';
import { GDTOutgoingTemplateDesigner } from './components/documents/GDTOutgoingTemplateDesigner';
import { HL7OutgoingTemplateDesigner } from './components/documents/HL7OutgoingTemplateDesigner';
import { DocumentTemplateRulesPage } from './components/documents/rules/DocumentTemplateRulesPage';
import { GDTMappingPage } from './components/gdt/mappings/edit/GDTMappingPage';
import { GDTMessagesPage } from './components/gdt/messages/GDTMessagesPage';
import { HL7FiltersPage } from './components/hl7/filters/HL7FiltersPage';
import { HL7MappingPage } from './components/hl7/mappings/edit/HL7MappingPage';
import { HL7MessageTypes } from './components/hl7/message-types/HL7MessageTypes';
import { HL7MessagesPage } from './components/hl7/messages/HL7MessagesPage';
import { MappingsPage } from './components/mappings/MappingsPage';
import { DataBlockFields } from './components/measurement-fields/DataBlockFields';
import { DataBlockTypes } from './components/measurement-types/DataBlockTypes';
import { DataBlockConfig } from './components/measurements/DataBlockConfig';
import { MergeAssistant } from './components/merge-assistant/MergeAssistantPage';
import ResourcesPage from './components/resources/ResourcesPage';
import { RolesPage } from './components/roles/RolesPage';
import ExtendedSearchPage from './components/search/ExtendedSearchPage';
import { CustomDictionaryPage } from './components/settings/CustomDictionary/CustomDictionaryPage';
import { SettingsPage } from './components/settings/SettingsPage';
import { SubtenantsPage } from './components/subtenants/SubtenantsPage';
import { SubtenantSettingsPage } from './components/tenant-settings/SubtenantSettingsPage';
import { TenantSettingsPage } from './components/tenant-settings/TenantSettingsPage';
import { UserList } from './components/users/list/UserList';
import { VisitTypeList } from './components/visit-types/list/VisitTypeList';
import { DocumentViewer } from './components/documents/DocumentViewer';
import { DocumentViewerMode } from './components/documents/types';
import { dictionaryKeys } from './components/dictionaries/dictionaries.types';
import { DictionaryRecordList } from './components/dictionaries/dictionary-record-list/DictionaryRecordList';
import { ToastContainer } from 'react-toastify';
import { HL7TemplateRulesPage } from './components/hl7/template-rules/HL7TemplateRulesPage';
import { RestoreSelectedTab } from './helpers/RestoreSelectedTab';
import { DoctorType } from '@lmt/shared-types';
import { GDTTemplateRulesPage } from './components/gdt/template-rules/GDTTemplateRulesPage';
import { LogUrlChangesComponent } from './helpers/router/LogUrlChangesComponent';
import { WorkstationSettingsPage } from './components/workstation-settings/WorkstationSettingsPage';
import i18next from 'i18next';
import { useLogOutAfterTabClosing } from './components/screen-plugs/useLogOutAfterTabClosing';

export const AppRouter: React.FC<
  React.PropsWithChildren<{
    appType: 'desktop' | 'web';
    additionalComponents?: React.FC;
    backendStartFailed: boolean;
  }>
> = (props) => {
  const isAuth = useIsAuthorized();

  const isLoggingOut = useLogOutAfterTabClosing();

  if (isAuth === 'loading' || isLoggingOut)
    return (
      <Loading
        loading={true}
        title={i18next.t('translation:load.loading', 'Loading...')}
      />
    );

  const routes = isAuth ? authorizedRoutes(props) : anonymousRoutes(props);
  return (
    <RouterProvider
      router={
        props.appType === 'web'
          ? createBrowserRouter(routes)
          : createHashRouter(routes)
      }
    />
  );
};

const RootPage = React.memo(
  (props: { additionalComponents?: React.FC; backendStartFailed: boolean }) => {
    const { additionalComponents, backendStartFailed } = props;
    return (
      <Launcher backendStartFailed={backendStartFailed ?? false}>
        <RestoreSelectedTab />
        <LogUrlChangesComponent />
        <LaunchedApp additionalComponents={additionalComponents} />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          icon={false}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </Launcher>
    );
  },
);

const LaunchedApp = withStartErrorCheck<{ additionalComponents?: React.FC }>()((
  props,
) => {
  const AdditionalComponents = props.additionalComponents;
  const i18n = useTranslation();

  return (
    <div className={appStyles.rootWrapper} key={i18n.i18n.language}>
      <React.Suspense
        fallback={<Loading loading={true} title={i18n.t('load.loading')} />}
      >
        {AdditionalComponents && <AdditionalComponents />}
        <Outlet />
      </React.Suspense>
    </div>
  );
});
export const authorizedRoutes = (props: {
  additionalComponents?: React.FC;
  backendStartFailed: boolean;
}): RouteObject[] => [
  {
    path: '/',
    element: <RootPage {...props} />,
    ErrorBoundary: ReactRouterErrorBoundary,
    children: [
      {
        path: '',
        element: <AuthenticatedApp />,
        children: [
          {
            path: trimStartPath(pages.patients.route),
            element: <PatientListPage />,
          },
          {
            path: trimStartPath(pages.calendar.route),
            element: <CalendarPage />,
          },
          {
            path: trimStartPath(pages.dashboard.route),
            element: <Dashboard />,
          },
          {
            index: true,
            element: <Dashboard />,
          },
          {
            path: trimStartPath(pages.patientsTab.route),
            element: <PatientListPage />,
          },
          {
            path: trimStartPath(pages.patientProfile.route),
            element: <PatientProfile />,
          },
          {
            path: trimStartPath(pages.patientsTab.route),
            element: <PatientListPage />,
          },
          {
            path: trimStartPath(pages.calendarTab.route),
            element: <CalendarPage />,
          },
          {
            path: trimStartPath(pages.patientProfile.route),
            element: <PatientProfile />,
          },
          { path: trimStartPath(pages.users.route), element: <UserList /> },
          { path: trimStartPath(pages.roles.route), element: <RolesPage /> },
          { path: trimStartPath(pages.audit.route), element: <AuditPage /> },
          {
            path: trimStartPath(pages.mappings.route),
            element: <MappingsPage />,
          },

          {
            path: trimStartPath(pages.subtenants.route),
            element: <SubtenantsPage />,
          },

          {
            path: trimStartPath(pages.dataBlockSettings.route),
            element: <DataBlockConfig />,
          },

          {
            path: trimStartPath(pages.statisticsDesigner.route),
            element: <StatisticsDesigner />,
          },

          {
            path: trimStartPath(pages.statisticsViewer.route),
            element: <StatisticsViewer />,
          },

          {
            path: trimStartPath(pages.documentDesigner.route),
            element: <DocumentDesigner />,
          },

          {
            path: trimStartPath(pages.createPatientDocument.route),
            element: <CreatePatientDocument />,
          },

          {
            path: trimStartPath(pages.documentTemplateRules.route),
            element: <DocumentTemplateRulesPage />,
          },

          {
            path: trimStartPath(`${pages.dataBlockFields.route}`),
            element: <DataBlockFields />,
          },

          {
            path: trimStartPath(pages.dataBlockTypes.route),
            element: <DataBlockTypes />,
          },
          {
            path: trimStartPath(pages.dictionaryTags.route),
            element: <DictionaryRecordList dictionary={dictionaryKeys.tags} />,
          },
          {
            path: trimStartPath(pages.dictionaryVisitTypes.route),
            element: <VisitTypeList />,
          },
          {
            path: trimStartPath(pages.hl7Messages.route),
            element: <HL7MessagesPage />,
          },
          {
            path: trimStartPath(pages.hl7Filters.route),
            element: <HL7FiltersPage />,
          },

          {
            path: trimStartPath(pages.hl7Mappings.route),
            element: <HL7MappingPage />,
          },
          {
            path: trimStartPath(pages.hl7MessageTypes.route),
            element: <HL7MessageTypes />,
          },

          {
            path: trimStartPath(pages.hl7OutgoingTemplates.route),
            element: <HL7OutgoingTemplateDesigner />,
          },
          {
            path: trimStartPath(pages.hl7TemplateRules.route),
            element: <HL7TemplateRulesPage />,
          },
          {
            path: trimStartPath(pages.gdtOutgoingTemplates.route),
            element: <GDTOutgoingTemplateDesigner />,
          },
          {
            path: trimStartPath(pages.gdtMessages.route),
            element: <GDTMessagesPage />,
          },
          {
            path: trimStartPath(pages.gdtMappings.route),
            element: <GDTMappingPage />,
          },
          {
            path: trimStartPath(pages.gdtTemplateRules.route),
            element: <GDTTemplateRulesPage />,
          },

          {
            path: trimStartPath(pages.doctors.route),
            element: <DoctorsList doctorType={DoctorType.External} />,
          },
          {
            path: trimStartPath(pages.attendingDoctors.route),
            element: <DoctorsList doctorType={DoctorType.Clinic} />,
          },

          {
            path: trimStartPath(pages.calendarColorsSettings.route),
            element: <ColorSettingsPage />,
          },

          {
            path: trimStartPath(pages.calendarVisitCardSettings.route),
            element: <VisitCardSettingsPage />,
          },

          {
            path: trimStartPath(pages.customDictionary.route),
            element: <CustomDictionaryPage />,
          },

          {
            path: trimStartPath(pages.settings.route),
            element: <SettingsPage />,
          },

          {
            path: trimStartPath(pages.tenantSettings.route),
            element: <TenantSettingsPage />,
          },

          {
            path: trimStartPath(pages.subtenantSettings.route),
            element: <SubtenantSettingsPage />,
          },

          {
            path: trimStartPath(pages.workstationSettings.route),
            element: <WorkstationSettingsPage />,
          },

          {
            path: trimStartPath(pages.resources.route),
            element: <ResourcesPage />,
          },

          {
            path: trimStartPath(`${pages.patientSearch.route}`),
            element: <ExtendedSearchPage />,
          },

          {
            path: trimStartPath(`${pages.mergeAssistant.route}`),
            element: <MergeAssistant />,
          },

          {
            path: trimStartPath(`${pages.absences.route}`),
            element: <AbsencesAndHolidaysPage />,
          },
        ],
      },
    ],
  },
];

function trimStartPath(path: string): string {
  return path && path[0] === '/' ? path.substring(1) : path;
}
export const anonymousRoutes = (props: {
  additionalComponents?: React.FC;
  backendStartFailed: boolean;
}): RouteObject[] => [
  {
    path: '/',
    element: <RootPage {...props} />,
    ErrorBoundary: ReactRouterErrorBoundary,
    children: [
      { path: '', element: <WelcomePage /> },
      { path: pages.createTenant.route, element: <WelcomePage /> },
      { path: pages.passwordReset.route, element: <ResetPasswordPage /> },
      { path: '*', element: <WelcomePage /> },
    ],
  },
];

const DocumentDesigner = () => {
  /// TODO: use useMatch when react-router properly handles '?' in the route
  const templateId = pages.documentDesigner.useParams().templateId;
  return (
    <DocumentViewer
      mode={DocumentViewerMode.Designer}
      templateId={templateId}
    />
  );
};

const CreatePatientDocument = () => {
  /// TODO: use useMatch when react-router properly handles '?' in the route
  const params = pages.createPatientDocument.useParams();
  return (
    <DocumentViewer
      mode={DocumentViewerMode.Viewer}
      patientId={params.patientId!}
      templateId={params.templateId}
    />
  );
};

const StatisticsDesigner = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "statistics-designer" */ '@lmt/unicorn-shared/app/components/statistics/StatisticsDesigner'
    ),
);
const StatisticsViewer = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "statistics-viewer" */ '@lmt/unicorn-shared/app/components/statistics/StatisticsViewer'
    ),
);
